import React, { useState } from 'react';
import { WordComponentsData } from '../types/analytics.types';
import './WordComponentAnalysis.css';

interface WordComponentAnalysisProps {
  data: WordComponentsData;
  loading?: boolean;
}

type ViewMode = 'low_success' | 'high_volume';

export const WordComponentAnalysis: React.FC<WordComponentAnalysisProps> = ({ data, loading }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('low_success');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterComponent, setFilterComponent] = useState<string>('all');

  if (loading) {
    return (
      <div className="word-component-analysis">
        <h4>Word + Component Analysis</h4>
        <div className="loading-skeleton" style={{ height: '400px' }}></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="word-component-analysis">
        <h4>Word + Component Analysis</h4>
        <p className="no-data">No word-component data available</p>
      </div>
    );
  }

  const displayData = viewMode === 'low_success'
    ? data.low_success_combinations || []
    : data.high_volume_combinations || [];

  // Get unique components for filter
  const allComponents = Array.from(new Set(displayData.map(d => d.component)));

  // Apply filters
  const filteredData = displayData.filter(item => {
    const matchesSearch = item.word_id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesComponent = filterComponent === 'all' || item.component === filterComponent;
    return matchesSearch && matchesComponent;
  });

  const getSuccessColor = (rate: number) => {
    if (rate >= 70) return 'success-high';
    if (rate >= 40) return 'success-medium';
    return 'success-low';
  };

  return (
    <div className="word-component-analysis">
      <div className="analysis-header">
        <div className="header-title">
          <h4>🔍 Word + Component Analysis</h4>
          <p className="header-subtitle">
            Identify word+component combinations with low success rates (&lt;70%) to find faulty metadata
          </p>
        </div>
        <div className="view-mode-selector">
          <button
            className={viewMode === 'low_success' ? 'active' : ''}
            onClick={() => setViewMode('low_success')}
          >
            ⚠️ Low Success Rate
          </button>
          <button
            className={viewMode === 'high_volume' ? 'active' : ''}
            onClick={() => setViewMode('high_volume')}
          >
            📊 High Volume
          </button>
        </div>
      </div>

      <div className="analysis-filters">
        <input
          type="text"
          placeholder="Search by word ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <select
          value={filterComponent}
          onChange={(e) => setFilterComponent(e.target.value)}
          className="component-filter"
        >
          <option value="all">All Components</option>
          {allComponents.map(comp => (
            <option key={comp} value={comp}>{comp}</option>
          ))}
        </select>
      </div>

      {filteredData.length === 0 ? (
        <p className="no-data">
          {viewMode === 'low_success'
            ? 'No problematic word-component combinations found - great job!'
            : 'No high volume combinations available'}
        </p>
      ) : (
        <>
          <div className="combinations-table-container">
            <table className="combinations-table">
              <thead>
                <tr>
                  <th className="rank-col">#</th>
                  <th className="word-col">Word ID</th>
                  <th className="component-col">Component</th>
                  <th className="attempts-col">Attempts</th>
                  <th className="users-col">Users</th>
                  <th className="success-col">Success Rate</th>
                  <th className="results-col">Correct/Incorrect</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item, index) => (
                  <tr key={`${item.word_id}-${item.component}`}>
                    <td className="rank-cell">{index + 1}</td>
                    <td className="word-cell" title={item.word_id}>
                      <code>{item.word_id.length > 25
                        ? item.word_id.substring(0, 25) + '...'
                        : item.word_id}
                      </code>
                    </td>
                    <td className="component-cell">
                      <span className="component-badge">{item.component}</span>
                    </td>
                    <td className="attempts-cell">{item.total_attempts}</td>
                    <td className="users-cell">{item.unique_users}</td>
                    <td className="success-cell">
                      <span className={`success-badge ${getSuccessColor(item.success_rate)}`}>
                        {item.success_rate.toFixed(1)}%
                      </span>
                    </td>
                    <td className="results-cell">
                      <span className="correct-count">✅ {item.correct}</span>
                      <span className="incorrect-count">❌ {item.incorrect}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="analysis-summary">
            <div className="summary-item">
              <span className="summary-label">Showing:</span>
              <span className="summary-value">{filteredData.length} combinations</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Total Combinations:</span>
              <span className="summary-value">{data.total_combinations}</span>
            </div>
            {viewMode === 'low_success' && filteredData.length > 0 && (
              <div className="summary-item alert">
                <span className="summary-icon">⚠️</span>
                <span className="summary-text">
                  These combinations likely have faulty metadata and should be reviewed
                </span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default WordComponentAnalysis;
